import {
  DropDownRef,
  DropDownSelectBox,
  DropDownSelectBoxOption,
  TokenListItem,
  WalletTokenListItem,
} from '@/components';
import { TokenInterface } from '@/interfaces';
import { WalletTokenInterface } from '@/interfaces/WalletTokenInterface';
import { logError, logInfo, logWarning } from '@/utils/logger';
import React, { ReactNode, useCallback, useMemo } from 'react';
import {
  FieldErrors,
  useForm,
  UseFormClearErrors,
  UseFormRegister,
  UseFormSetValue,
} from 'react-hook-form';
type OptionValue = string | number;
interface TokenSelectionDropDownSelectBoxProps {
  name: string;
  tokensToBeListedAsOptions: TokenInterface[];
  tokenToInvest?: TokenInterface;
  onTokenSelect: (
    param:
      | React.ChangeEvent<HTMLInputElement>
      | DropDownSelectBoxOption<string>,
  ) => void;
  register?: UseFormRegister<any>;
  setValue?: UseFormSetValue<any>;
  clearErrors?: UseFormClearErrors<any>;
  errors?: FieldErrors;
  label?: string;
  tooltip?: JSX.Element;
  tooltipIsFullBleed?: boolean;
  extraButton?: ReactNode;
  extraButtonPosition?: 'top' | 'bottom';
  closeDropdown?: boolean;
  preventCloseOnOutsideClick?: boolean;
}
const isWalletToken = (
  token: TokenInterface,
): token is WalletTokenInterface => {
  return 'balance' in token;
};
const TokenSelectionDropDownSelectBox = React.forwardRef<
  DropDownRef,
  TokenSelectionDropDownSelectBoxProps
>(
  (
    {
      name,
      label = 'Select a Token',
      tokensToBeListedAsOptions,
      tokenToInvest,
      errors,
      onTokenSelect,
      register,
      setValue,
      clearErrors,
      extraButton,
      extraButtonPosition,
      closeDropdown = false,
      preventCloseOnOutsideClick = false,
      ...rest
    },
    ref,
  ) => {
    const form = useForm();
    const safeRegister = register || form.register;
    const safeSetValue = setValue || form.setValue;
    const safeClearErrors = clearErrors || form.clearErrors;
    const { tokenOptions, displayOptions, selectedDisplayOptions } =
      useMemo(() => {
        try {
          const options = tokensToBeListedAsOptions
            .filter((token) => token.id)
            .map(({ id }) => ({
              value: id,
            }));
          const displays = tokensToBeListedAsOptions
            .filter((token) => token.id)
            .map((token) => {
              return isWalletToken(token) ? (
                <WalletTokenListItem
                  key={token.id}
                  token={token}
                  showWalletAddress={false}
                  showBalance
                  showBalancePrefix
                  showTicker
                  showNetwork
                  showNetworkPrefix
                  showSubNetworkNextToMainNetwork
                />
              ) : (
                <TokenListItem
                  key={token.id}
                  token={token}
                  showTicker
                  showNetwork
                  showNetworkPrefix
                  showSubNetworkNextToMainNetwork
                />
              );
            });
          const selectedDisplays = tokensToBeListedAsOptions
            .filter((token) => token.id)
            .map((token) => {
              return isWalletToken(token) ? (
                <WalletTokenListItem
                  key={token.id}
                  token={token}
                  showWalletAddress={false}
                  showBalance
                  showBalancePrefix
                  showTicker
                  showNetwork
                  showNetworkPrefix
                  showSubNetworkNextToMainNetwork
                />
              ) : (
                <TokenListItem
                  key={token.id}
                  token={token}
                  showTicker
                  showNetwork
                  showNetworkPrefix
                  showSubNetworkNextToMainNetwork
                />
              );
            });
          return {
            tokenOptions: options,
            displayOptions: displays,
            selectedDisplayOptions: selectedDisplays,
          };
        } catch (error) {
          logError(
            error,
            'TokenSelectionDropDownSelectBox.useMemo',
            tokensToBeListedAsOptions,
          );
          return {
            tokenOptions: [],
            displayOptions: [],
            selectedDisplayOptions: [],
          };
        }
      }, [tokensToBeListedAsOptions]);
    const handleTokenSelect = useCallback(
      (
        param:
          | React.ChangeEvent<HTMLInputElement>
          | DropDownSelectBoxOption<OptionValue>,
      ) => {
        try {
          if ('target' in param) {
            logWarning(
              'Unexpected input event received in token selection.',
              'TokenSelectionDropDownSelectBox.handleTokenSelect',
              param,
            );
            return;
          }
          const selectedOption = param;
          const selectedToken = tokensToBeListedAsOptions.find(
            (token) => token.id === selectedOption.value,
          );
          if (selectedToken) {
            logInfo(
              'Token selected successfully.',
              'TokenSelectionDropDownSelectBox.handleTokenSelect',
              selectedToken,
            );
            safeSetValue(name, selectedToken, { shouldValidate: true });
            onTokenSelect({ value: selectedToken.id });
            safeClearErrors(name);
          } else {
            logWarning(
              'Selected token not found in options.',
              'TokenSelectionDropDownSelectBox.handleTokenSelect',
              selectedOption,
            );
          }
        } catch (error) {
          logError(
            error,
            'TokenSelectionDropDownSelectBox.handleTokenSelect',
            param,
          );
        }
      },
      [
        onTokenSelect,
        tokensToBeListedAsOptions,
        name,
        safeSetValue,
        safeClearErrors,
      ],
    );
    return (
      <DropDownSelectBox
        ref={ref}
        label={label}
        name={name}
        options={tokenOptions}
        displayOptions={displayOptions}
        selectedDisplayOptions={selectedDisplayOptions}
        value={tokenToInvest?.id || ''}
        onChange={(param) => {
          handleTokenSelect(param);
        }}
        register={safeRegister}
        setValue={safeSetValue}
        clearErrors={safeClearErrors}
        errors={errors}
        extraButton={extraButton}
        extraButtonPosition={extraButtonPosition}
        closeDropdown={closeDropdown}
        preventCloseOnOutsideClick={preventCloseOnOutsideClick}
        {...rest}
      />
    );
  },
);
export default TokenSelectionDropDownSelectBox;
